import { useCallback, useEffect, useRef, useState } from 'react';

import { CheckIcon, CopyIcon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';

const COPY_RESET_DELAY = 1500;

export type APIKeyCreatedData = { apiKey: string; name: string; type: string };

type ModalAPIKeyCreatedProps = { data: APIKeyCreatedData | null; onClose: () => void };

export const ModalAPIKeyCreated = ({ data, onClose }: ModalAPIKeyCreatedProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setCopied(false);
        onClose();
      }
    },
    [onClose]
  );

  const handleCopy = useCallback(async () => {
    if (!data) {
      return;
    }

    try {
      await navigator.clipboard.writeText(data.apiKey);
      trackEvent(EventName.ClickCopyAPIKey);
      toast.success(t('developer_tools.api_modal.copied_toast', { 0: data.name }));
      setCopied(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, COPY_RESET_DELAY);
    } catch {
      toast.error(t('common.error'));
    }
  }, [data, t]);

  return (
    <Dialog open={!!data} onOpenChange={handleOpenChange}>
      <DialogContent
        className="gap-6 sm:max-w-md"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('developer_tools.api_modal.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant="warning" className="gap-y-1">
            <TriangleAlertIcon size={16} />
            <AlertTitle>{t('developer_tools.api_modal.warning_title')}</AlertTitle>
            <AlertDescription>
              {t('developer_tools.api_modal.warning_description')}
            </AlertDescription>
          </Alert>

          <div className="space-y-1.5">
            <Label>{t('developer_tools.api_modal.api_key_label')}</Label>
            <div className="relative">
              <Input value={data?.apiKey ?? ''} readOnly tabIndex={-1} className="pr-9 text-xs" />
              <Button
                variant="ghost-neutral"
                size="icon-sm"
                className="absolute top-1/2 right-2 -translate-y-1/2"
                onClick={handleCopy}
              >
                {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground-muted">{t('common.name')}</span>
              <span className="text-sm font-medium">{data?.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground-muted">{t('common.type')}</span>
              <span className="text-sm font-medium">{data?.type}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            autoFocus
            className="focus:ring-2 focus:ring-ring focus:ring-offset-2"
            onClick={handleCopy}
          >
            {copied ? (
              <>
                <CheckIcon size={16} />
                {t('developer_tools.api_modal.copied_button')}
              </>
            ) : (
              <>
                <CopyIcon size={16} />
                {t('developer_tools.api_modal.copy_button')}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
